import React, { useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { isStandalone, isIOS, permiso, pushSupported, estadoServidor, probarAviso } from "@/lib/push";

const Fila = ({ ok, warn, label, detalle, testid }) => {
  const I = ok ? Icons.CheckCircle2 : warn ? Icons.AlertTriangle : Icons.XCircle;
  const c = ok ? "text-[#15803D]" : warn ? "text-[#B45309]" : "text-[#B91C1C]";
  return (
    <li data-testid={testid} data-ok={ok ? "si" : "no"} className="flex items-start gap-3 py-3 border-b border-[#F2EDE4] last:border-0">
      <I size={24} className={`${c} shrink-0`} />
      <div><p className="text-base font-semibold text-[#1C2421]">{label}</p>{detalle && <p className="text-sm text-[#6E7A74]">{detalle}</p>}</div>
    </li>
  );
};

// "Comprobar instalación y avisos": app instalada, permiso, registro en servidor y aviso de prueba.
export const ComprobarAvisos = () => {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prueba, setPrueba] = useState(null); // null | enviada | llego | no_llego | error

  const comprobar = async () => {
    setBusy(true);
    try {
      const srv = pushSupported() ? await estadoServidor() : { registrada: false, preferencias: {} };
      setR({ standalone: isStandalone(), perm: permiso(), srv });
    } catch { setR({ standalone: isStandalone(), perm: permiso(), srv: { registrada: false, preferencias: {} } }); }
    finally { setBusy(false); }
  };
  const enviar = async () => {
    try { await probarAviso(); setPrueba("enviada"); }
    catch (err) { setPrueba("error"); toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar el aviso de prueba."); }
  };

  const ios = isIOS();
  return (
    <section className="bg-white border border-[#E5DFD5] rounded-2xl p-5" data-testid="comprobar-avisos">
      <button data-testid="comprobar-avisos-btn" onClick={comprobar} disabled={busy}
        className="w-full min-h-[56px] rounded-xl bg-[#1C2421] text-white font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Icons.Loader2 size={20} className="animate-spin" /> : <Icons.ClipboardCheck size={20} />} Comprobar instalación y avisos
      </button>
      {r && (
        <>
          <ul className="mt-4" data-testid="comprobar-resultado">
            <Fila testid="check-instalada" ok={r.standalone} warn={!r.standalone && !ios}
              label={r.standalone ? "AL DÍA está abierta como app instalada" : "AL DÍA no está abierta como app instalada"}
              detalle={r.standalone ? null : ios ? "En iPhone es imprescindible: añádela a la pantalla de inicio y ábrela desde el icono." : "Recomendado: instálala para abrirla más fácilmente."} />
            <Fila testid="check-permiso" ok={r.perm === "granted"}
              label={r.perm === "granted" ? "Notificaciones permitidas" : r.perm === "denied" ? "Notificaciones bloqueadas" : r.perm === "unsupported" ? (ios ? "En iPhone, los avisos solo funcionan desde la app instalada" : "Este navegador no admite notificaciones aquí") : "Notificaciones aún no activadas"}
              detalle={r.perm === "denied" ? (ios ? "Actívalas en Ajustes > Notificaciones > AL DÍA." : "Actívalas en los ajustes del teléfono, en Notificaciones de AL DÍA o de Chrome.") : null} />
            <Fila testid="check-registro" ok={r.srv.registrada}
              label={r.srv.registrada ? "Este dispositivo está registrado para recibir avisos" : "Este dispositivo no está registrado"}
              detalle={r.srv.registrada ? null : r.srv.motivo === "caducada" ? "El permiso se retiró o se borró el icono. Vuelve a activar los avisos." : "Pulsa «Activar avisos»."} />
          </ul>
          {r.srv.registrada && r.perm === "granted" && (
            <div className="mt-4 space-y-2" data-testid="comprobar-prueba">
              {prueba === null || prueba === "error" ? (
                <button data-testid="comprobar-probar-aviso" onClick={enviar}
                  className="w-full min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg flex items-center justify-center gap-2">
                  <Icons.BellRing size={20} /> Probar aviso
                </button>
              ) : prueba === "enviada" ? (
                <div className="bg-[#F6F3EE] rounded-xl p-4">
                  <p className="text-base font-semibold text-[#1C2421]">¿Te ha llegado «AL DÍA: aviso de prueba»?</p>
                  <div className="flex gap-2 mt-3">
                    <button data-testid="comprobar-llego-si" onClick={() => setPrueba("llego")} className="flex-1 min-h-[48px] rounded-xl bg-[#3A6B56] text-white font-semibold">Sí, me ha llegado</button>
                    <button data-testid="comprobar-llego-no" onClick={() => setPrueba("no_llego")} className="flex-1 min-h-[48px] rounded-xl bg-white border border-[#E5DFD5] font-semibold text-[#4A5550]">No</button>
                  </div>
                </div>
              ) : prueba === "llego" ? (
                <p data-testid="comprobar-ok" className="bg-[#DCFCE7] text-[#166534] rounded-xl p-4 text-base font-semibold flex items-center gap-2">
                  <Icons.PartyPopper size={20} /> Todo correcto: este dispositivo recibirá los avisos de AL DÍA.
                </p>
              ) : (
                <div data-testid="comprobar-no-llego" className="bg-[#FFFBEB] border border-[#FDE68A] rounded-xl p-4 text-sm text-[#92400E] space-y-1">
                  <p className="font-bold">Algunas cosas que puedes revisar:</p>
                  <p>• Que el modo «No molestar» o «Concentración» no esté activado.</p>
                  {!ios && <p>• Que el ahorro de batería no limite a AL DÍA o a Chrome.</p>}
                  <p>• Espera un minuto y vuelve a probar. Si sigue sin llegar, pulsa «Activar avisos» otra vez.</p>
                  <button onClick={() => setPrueba(null)} className="mt-2 font-semibold underline" data-testid="comprobar-reintentar">Volver a probar</button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
};
